import dayjs from 'dayjs'

export const GROUPING_OPTIONS = [
  { value: 'Account', label: 'Account' },
  { value: 'Account Type', label: 'Account Type' },
  { value: 'Asset', label: 'Asset' },
  { value: 'Asset Category', label: 'Asset Category' },
  { value: 'Asset Class', label: 'Asset Class' },
  { value: 'Custodian', label: 'Custodian' },
  { value: 'Fund Family', label: 'Fund Family' },
  { value: 'Household', label: 'Household' },
  { value: 'Management Style', label: 'Management Style' },
  { value: 'Product', label: 'Product' },
  { value: 'Product Type', label: 'Product Type' },
  { value: 'Registration', label: 'Registration' },
  { value: 'Risk Category', label: 'Risk Category' }
]

// All grouping types that correspond to real Orion CalculationEntity values.
// Used to build entity-specific filter paths (e.g., group-info:Product:Name).
export const ORION_ENTITY_GROUPINGS = new Set([
  'Account', 'Registration', 'Household',
  'Product', 'Asset', 'Representative', 'BrokerDealer'
])

// Only household-owned entities are safe to re-scope directly on expand.
// Firm-wide entities (Product, Asset, Representative, BrokerDealer) must use
// the filtered approach to preserve the household scope.
export const ENTITY_SCOPABLE_GROUPINGS = new Set([
  'Account', 'Registration', 'Household'
])

export const QUICK_DATE_OPTIONS = [
  { value: 'Day', label: 'Day' },
  { value: 'WTD', label: 'WTD' },
  { value: 'MTD', label: 'MTD' },
  { value: 'QTD', label: 'QTD' },
  { value: 'YTD', label: 'YTD' },
  { value: 'One Year', label: '1 Year' },
  { value: 'Two Year', label: '2 Year' },
  { value: 'Three Year', label: '3 Year' },
  { value: 'Five Year', label: '5 Year' },
  { value: 'Ten Year', label: '10 Year' },
  { value: 'Previous 1-Year', label: 'Prev 1 Year' },
  { value: 'Previous 2-Year', label: 'Prev 2 Year' },
  { value: 'Previous 3-Year', label: 'Prev 3 Year' },
  { value: 'Previous 5-Year', label: 'Prev 5 Year' },
  { value: 'Previous 10-Year', label: 'Prev 10 Year' },
  { value: 'Inception', label: 'Inception' },
  { value: 'Holding Period', label: 'Holding Period' }
]

export const DATE_MODE_OPTIONS = [
  { value: 'default', label: 'Default' },
  ...QUICK_DATE_OPTIONS.map(o => ({ value: `qd:${o.value}`, label: o.label })),
  { value: 'dateRange', label: 'Custom Range...' }
]

/**
 * Builds a contextChange date fragment from column options.
 * Returns { quickDate } for presets, { dateRange } for custom ranges, or undefined for default.
 */
export function buildDateContextChange (options) {
  if (!options) return undefined
  const mode = options.dateMode

  if (mode === 'quickDate' && options.quickDate) {
    return { quickDate: options.quickDate }
  }
  if (mode === 'dateRange' && options.startDate && options.endDate) {
    return {
      dateRange: {
        $type: 'date-range',
        startDate: options.startDate,
        endDate: options.endDate
      }
    }
  }
  return undefined
}

/**
 * Builds a column header label that reflects the date configuration.
 */
export function buildDateColumnHeader (fullLabel, shortLabel, options) {
  if (!options) return fullLabel
  const mode = options.dateMode

  if (mode === 'quickDate' && options.quickDate) {
    return `${shortLabel} ${options.quickDate}`
  }
  if (mode === 'dateRange' && options.startDate && options.endDate) {
    const start = dayjs(options.startDate).format('M/D/YY')
    const end = dayjs(options.endDate).format('M/D/YY')
    return `${shortLabel} ${start}-${end}`
  }
  return fullLabel
}

const COST_BASIS_SETTINGS = [
  {
    key: 'source',
    label: 'Source',
    control: 'select',
    options: [
      { value: 'Orion', label: 'Orion' },
      { value: 'Custodian', label: 'Custodian' }
    ],
    defaultValue: 'Orion'
  },
  {
    key: 'realizationType',
    label: 'Type',
    control: 'select',
    options: [
      { value: 'Unrealized', label: 'Unrealized' },
      { value: 'Realized', label: 'Realized' }
    ],
    defaultValue: 'Unrealized'
  }
]

const COST_BASIS_DEFAULT_OPTIONS = {
  source: 'Orion',
  realizationType: 'Unrealized'
}

function buildCostBasisContextChange (options) {
  const hasCostBasisSettings = options.source || options.realizationType
  return hasCostBasisSettings
    ? {
      costBasisOption: {
        $type: 'cost-basis',
        ...(options.source && { source: options.source }),
        ...(options.realizationType && { realizationType: options.realizationType })
      }
    }
    : undefined
}

const STATISTIC_TYPE_OPTIONS = [
  { value: 'Standard Deviation', label: 'Standard Deviation' },
  { value: 'Mean', label: 'Mean' },
  { value: 'Variance', label: 'Variance' },
  { value: 'Drawdown', label: 'Drawdown' },
  { value: 'Sharpe Ratio', label: 'Sharpe Ratio' },
  { value: 'Sortino Ratio', label: 'Sortino Ratio' },
  { value: 'Covariance', label: 'Covariance' },
  { value: 'R-Squared', label: 'R-Squared' },
  { value: 'Beta Coefficient', label: 'Beta Coefficient' },
  { value: 'Treynor Ratio', label: 'Treynor Ratio' },
  { value: "Jensen's Alpha", label: "Jensen's Alpha" },
  { value: 'Upside Capture Ratio', label: 'Upside Capture Ratio' },
  { value: 'Downside Capture Ratio', label: 'Downside Capture Ratio' }
]

const STATISTIC_SETTINGS = [
  {
    key: 'statisticType',
    label: 'Statistic',
    control: 'select',
    options: STATISTIC_TYPE_OPTIONS,
    defaultValue: 'Standard Deviation'
  }
]

const PERFORMANCE_DATE_DEFAULTS = {
  dateMode: 'default',
  quickDate: null,
  startDate: null,
  endDate: null
}

const PERFORMANCE_SETTINGS = [
  {
    key: 'calculationMethod',
    label: 'Method',
    control: 'select',
    options: [
      { value: 'TWR', label: 'TWR' },
      { value: 'Simple', label: 'Simple' },
      { value: 'IRR', label: 'IRR' }
    ],
    defaultValue: 'TWR'
  },
  { key: 'annualize', label: 'Annualize', control: 'checkbox', defaultValue: false },
  { key: 'calcGrossOfFees', label: 'Gross of Fees', control: 'checkbox', defaultValue: false },
  { key: 'calcAfterTax', label: 'After Tax', control: 'checkbox', defaultValue: false }
]

function buildPerformanceContextChange (options) {
  const dateContext = buildDateContextChange(options) || {}
  const hasSettings = options.calculationMethod || options.annualize || options.calcGrossOfFees || options.calcAfterTax
  if (hasSettings) {
    dateContext.performanceOption = {
      $type: 'performance',
      ...(options.calculationMethod && { calculationMethod: options.calculationMethod }),
      ...(options.annualize && { annualize: true }),
      ...(options.calcGrossOfFees && { calcGrossOfFees: true }),
      ...(options.calcAfterTax && { calcAfterTax: true })
    }
  }
  return Object.keys(dateContext).length > 0 ? dateContext : undefined
}

export const CALCULATION_TYPES = {
  marketValue: {
    type: 'marketValue',
    label: 'Market Value',
    orionType: 'activity',
    supportsDate: false,
    settingsSchema: [
      {
        key: 'valuationMethod',
        label: 'Valuation',
        control: 'select',
        options: [
          { value: 'Ending Market Value', label: 'Ending Market Value' },
          { value: 'Beginning Market Value', label: 'Beginning Market Value' },
          { value: 'Ending Bond Accrual', label: 'Ending Bond Accrual' },
          { value: 'Beginning Bond Accrual', label: 'Beginning Bond Accrual' }
        ],
        defaultValue: 'Ending Market Value'
      },
      { key: 'includeAccruedInterest', label: 'Include Accrued Interest', control: 'checkbox', defaultValue: true }
    ],
    defaultOptions: { valuationMethod: 'Ending Market Value', includeAccruedInterest: true },
    buildCalculation: (id, options) => ({
      $type: 'activity',
      id,
      contextChange: {
        activityOption: {
          $type: 'market-value',
          valuationMethod: options.valuationMethod || 'Ending Market Value',
          includeAccruedInterest: options.includeAccruedInterest ?? true
        }
      }
    }),
    columns: (id) => [
      { accessorKey: id, header: 'Market Value', format: 'marketValue', align: 'right' }
    ],
    extractValue: (calc) => calc.value
  },

  performance: {
    type: 'performance',
    label: 'Performance',
    orionType: 'performance',
    supportsDate: true,
    settingsSchema: PERFORMANCE_SETTINGS,
    defaultOptions: {
      dateMode: 'default',
      quickDate: null,
      startDate: null,
      endDate: null,
      calculationMethod: 'TWR',
      annualize: false,
      calcGrossOfFees: false,
      calcAfterTax: false
    },
    buildCalculation: (id, options) => ({
      $type: 'performance',
      id,
      contextChange: buildPerformanceContextChange(options)
    }),
    columns: (id, options) => [
      { accessorKey: id, header: buildDateColumnHeader('Performance', 'Perf', options), format: 'returns', align: 'right' }
    ],
    extractValue: (calc) => calc.performance
  },

  costBasis: {
    type: 'costBasis',
    label: 'Cost Basis',
    orionType: 'cost-basis',
    supportsDate: false,
    settingsSchema: COST_BASIS_SETTINGS,
    defaultOptions: { ...COST_BASIS_DEFAULT_OPTIONS },
    buildCalculation: (id, options) => ({
      $type: 'cost-basis',
      id,
      contextChange: buildCostBasisContextChange(options)
    }),
    columns: (id) => [
      { accessorKey: id, header: 'Cost Basis', format: 'marketValue', align: 'right' },
      { accessorKey: `${id}_gainLoss`, header: 'Gain/Loss', format: 'marketValue', align: 'right' }
    ],
    extractValue: (calc) => calc.costBasis,
    extractExtraValues: (calc, id) => ({ [`${id}_gainLoss`]: calc.gainLoss })
  },

  estimatedIncome: {
    type: 'estimatedIncome',
    label: 'Est. Annual Income',
    orionType: 'estimated-annual-income',
    supportsDate: false,
    settingsSchema: [],
    defaultOptions: {},
    buildCalculation: (id, _options) => ({
      $type: 'estimated-annual-income',
      id
    }),
    columns: (id) => [
      { accessorKey: id, header: 'Est. Annual Income', format: 'marketValue', align: 'right' }
    ],
    extractValue: (calc) => calc.estimatedAnnualIncome
  },

  performanceContribution: {
    type: 'performanceContribution',
    label: 'Contribution',
    orionType: 'performance-contribution',
    supportsDate: true,
    settingsSchema: PERFORMANCE_SETTINGS,
    defaultOptions: {
      dateMode: 'default',
      quickDate: null,
      startDate: null,
      endDate: null,
      calculationMethod: 'TWR',
      annualize: false,
      calcGrossOfFees: false,
      calcAfterTax: false
    },
    buildCalculation: (id, options) => ({
      $type: 'performance-contribution',
      id,
      contextChange: buildPerformanceContextChange(options)
    }),
    columns: (id, options) => [
      { accessorKey: id, header: buildDateColumnHeader('Contribution', 'Contrib', options), format: 'returns', align: 'right' },
      { accessorKey: `${id}_avgWeight`, header: 'Avg Weight', format: 'returns', align: 'right' }
    ],
    extractValue: (calc) => calc.contribution,
    extractExtraValues: (calc, id) => ({ [`${id}_avgWeight`]: calc.averageWeight })
  },

  shorttermCostBasis: {
    type: 'shorttermCostBasis',
    label: 'Short-Term Cost Basis',
    orionType: 'shortterm-cost-basis',
    supportsDate: false,
    settingsSchema: COST_BASIS_SETTINGS,
    defaultOptions: { ...COST_BASIS_DEFAULT_OPTIONS },
    buildCalculation: (id, options) => ({
      $type: 'shortterm-cost-basis',
      id,
      contextChange: buildCostBasisContextChange(options)
    }),
    columns: (id) => [
      { accessorKey: id, header: 'ST Cost Basis', format: 'marketValue', align: 'right' },
      { accessorKey: `${id}_gainLoss`, header: 'ST Gain/Loss', format: 'marketValue', align: 'right' }
    ],
    extractValue: (calc) => calc.costBasis,
    extractExtraValues: (calc, id) => ({ [`${id}_gainLoss`]: calc.gainLoss })
  },

  longtermCostBasis: {
    type: 'longtermCostBasis',
    label: 'Long-Term Cost Basis',
    orionType: 'longterm-cost-basis',
    supportsDate: false,
    settingsSchema: COST_BASIS_SETTINGS,
    defaultOptions: { ...COST_BASIS_DEFAULT_OPTIONS },
    buildCalculation: (id, options) => ({
      $type: 'longterm-cost-basis',
      id,
      contextChange: buildCostBasisContextChange(options)
    }),
    columns: (id) => [
      { accessorKey: id, header: 'LT Cost Basis', format: 'marketValue', align: 'right' },
      { accessorKey: `${id}_gainLoss`, header: 'LT Gain/Loss', format: 'marketValue', align: 'right' }
    ],
    extractValue: (calc) => calc.costBasis,
    extractExtraValues: (calc, id) => ({ [`${id}_gainLoss`]: calc.gainLoss })
  },

  benchmarkPerformance: {
    type: 'benchmarkPerformance',
    label: 'Benchmark Perf',
    orionType: 'benchmark-performance',
    supportsDate: true,
    settingsSchema: PERFORMANCE_SETTINGS,
    defaultOptions: {
      ...PERFORMANCE_DATE_DEFAULTS,
      calculationMethod: 'TWR',
      annualize: false,
      calcGrossOfFees: false,
      calcAfterTax: false
    },
    buildCalculation: (id, options) => ({
      $type: 'benchmark-performance',
      id,
      contextChange: buildPerformanceContextChange(options)
    }),
    columns: (id, options) => [
      { accessorKey: id, header: buildDateColumnHeader('Benchmark Perf', 'BM Perf', options), format: 'returns', align: 'right' }
    ],
    extractValue: (calc) => calc.performance
  },

  benchmarkAftertaxPerformance: {
    type: 'benchmarkAftertaxPerformance',
    label: 'Benchmark After-Tax Perf',
    orionType: 'benchmark-aftertax-performance',
    supportsDate: true,
    settingsSchema: [],
    defaultOptions: { ...PERFORMANCE_DATE_DEFAULTS },
    buildCalculation: (id, options) => ({
      $type: 'benchmark-aftertax-performance',
      id,
      contextChange: {
        ...buildDateContextChange(options),
        performanceOption: {
          $type: 'performance',
          calcAfterTax: true
        }
      }
    }),
    columns: (id, options) => [
      { accessorKey: id, header: buildDateColumnHeader('BM After-Tax', 'BM AT', options), format: 'returns', align: 'right' }
    ],
    extractValue: (calc) => calc.performance
  },

  attributionEffect: {
    type: 'attributionEffect',
    label: 'Attribution Effect',
    orionType: 'attribution-effect',
    supportsDate: true,
    settingsSchema: [],
    defaultOptions: { ...PERFORMANCE_DATE_DEFAULTS },
    buildCalculation: (id, options) => ({
      $type: 'attribution-effect',
      id,
      contextChange: buildDateContextChange(options)
    }),
    columns: (id, options) => [
      { accessorKey: `${id}_alloc`, header: buildDateColumnHeader('Allocation Effect', 'Alloc', options), format: 'returns', align: 'right' },
      { accessorKey: `${id}_select`, header: 'Selection Effect', format: 'returns', align: 'right' },
      { accessorKey: `${id}_interact`, header: 'Interaction Effect', format: 'returns', align: 'right' },
      { accessorKey: id, header: 'Total Effect', format: 'returns', align: 'right' }
    ],
    extractValue: (calc) => calc.attributionEffect?.totalEffect,
    extractExtraValues: (calc, id) => ({
      [`${id}_alloc`]: calc.attributionEffect?.allocationEffect,
      [`${id}_select`]: calc.attributionEffect?.selectionEffect,
      [`${id}_interact`]: calc.attributionEffect?.interactionEffect
    })
  },

  yield: {
    type: 'yield',
    label: 'Yield',
    orionType: 'yield',
    supportsDate: false,
    settingsSchema: [
      {
        key: 'yieldType',
        label: 'Yield Type',
        control: 'select',
        options: [
          { value: 'Current', label: 'Current' },
          { value: 'ToMaturityCost', label: 'To Maturity (Cost)' },
          { value: 'ToMaturityMarket', label: 'To Maturity (Market)' },
          { value: 'ToPrerefundedRedemption', label: 'To Prerefunded Redemption' },
          { value: 'ToNextCall', label: 'To Next Call' },
          { value: 'ToWorst', label: 'To Worst' },
          { value: 'OnCost', label: 'On Cost' },
          { value: 'ToBest', label: 'To Best' }
        ],
        defaultValue: 'Current'
      }
    ],
    defaultOptions: { yieldType: 'Current' },
    buildCalculation: (id, options) => ({
      $type: 'yield',
      id,
      ...(options.yieldType && { yieldType: options.yieldType })
    }),
    columns: (id, options) => [
      { accessorKey: id, header: `Yield (${options?.yieldType || 'Current'})`, format: 'returns', align: 'right' }
    ],
    extractValue: (calc) => calc.yield
  },

  currentYield: {
    type: 'currentYield',
    label: 'Current Yield',
    orionType: 'current-yield',
    supportsDate: false,
    settingsSchema: [],
    defaultOptions: {},
    buildCalculation: (id) => ({
      $type: 'current-yield',
      id
    }),
    columns: (id) => [
      { accessorKey: id, header: 'Current Yield', format: 'returns', align: 'right' }
    ],
    extractValue: (calc) => calc.yield
  },

  yieldOnCost: {
    type: 'yieldOnCost',
    label: 'Yield on Cost',
    orionType: 'yield-on-cost',
    supportsDate: false,
    settingsSchema: [],
    defaultOptions: {},
    buildCalculation: (id) => ({
      $type: 'yield-on-cost',
      id
    }),
    columns: (id) => [
      { accessorKey: id, header: 'Yield on Cost', format: 'returns', align: 'right' }
    ],
    extractValue: (calc) => calc.yield
  },

  yieldToMaturity: {
    type: 'yieldToMaturity',
    label: 'Yield to Maturity',
    orionType: 'yield-to-maturity',
    supportsDate: false,
    settingsSchema: [],
    defaultOptions: {},
    buildCalculation: (id) => ({
      $type: 'yield-to-maturity',
      id
    }),
    columns: (id) => [
      { accessorKey: id, header: 'YTM', format: 'returns', align: 'right' }
    ],
    extractValue: (calc) => calc.yield
  },

  yieldToNextCall: {
    type: 'yieldToNextCall',
    label: 'Yield to Next Call',
    orionType: 'yield-to-next-call',
    supportsDate: false,
    settingsSchema: [],
    defaultOptions: {},
    buildCalculation: (id) => ({
      $type: 'yield-to-next-call',
      id
    }),
    columns: (id) => [
      { accessorKey: id, header: 'YTC', format: 'returns', align: 'right' }
    ],
    extractValue: (calc) => calc.yield
  },

  yieldToWorst: {
    type: 'yieldToWorst',
    label: 'Yield to Worst',
    orionType: 'yield-to-worst',
    supportsDate: false,
    settingsSchema: [],
    defaultOptions: {},
    buildCalculation: (id) => ({
      $type: 'yield-to-worst',
      id
    }),
    columns: (id) => [
      { accessorKey: id, header: 'YTW', format: 'returns', align: 'right' }
    ],
    extractValue: (calc) => calc.yield
  },

  groupStatistic: {
    type: 'groupStatistic',
    label: 'Statistic',
    orionType: 'group-statistic',
    supportsDate: true,
    settingsSchema: STATISTIC_SETTINGS,
    defaultOptions: {
      ...PERFORMANCE_DATE_DEFAULTS,
      statisticType: 'Standard Deviation'
    },
    buildCalculation: (id, options) => ({
      $type: 'group-statistic',
      id,
      ...(options.statisticType && { statisticType: options.statisticType }),
      contextChange: buildDateContextChange(options)
    }),
    columns: (id, options) => [
      { accessorKey: id, header: options?.statisticType || 'Std Dev', format: 'returns', align: 'right' }
    ],
    extractValue: (calc) => calc.value
  },

  benchmarkStatistic: {
    type: 'benchmarkStatistic',
    label: 'Benchmark Statistic',
    orionType: 'benchmark-statistic',
    supportsDate: true,
    settingsSchema: STATISTIC_SETTINGS,
    defaultOptions: {
      ...PERFORMANCE_DATE_DEFAULTS,
      statisticType: 'Standard Deviation'
    },
    buildCalculation: (id, options) => ({
      $type: 'benchmark-statistic',
      id,
      ...(options.statisticType && { statisticType: options.statisticType }),
      contextChange: buildDateContextChange(options)
    }),
    columns: (id, options) => [
      { accessorKey: id, header: `BM ${options?.statisticType || 'Std Dev'}`, format: 'returns', align: 'right' }
    ],
    extractValue: (calc) => calc.value
  },

  taxAlpha: {
    type: 'taxAlpha',
    label: 'Tax Alpha',
    orionType: 'tax-alpha',
    supportsDate: true,
    settingsSchema: [
      {
        key: 'taxInceptionType',
        label: 'Inception Type',
        control: 'select',
        options: [
          { value: 'CostBasis', label: 'Cost Basis' },
          { value: 'NetDeposits', label: 'Net Deposits' }
        ],
        defaultValue: 'CostBasis'
      }
    ],
    defaultOptions: {
      ...PERFORMANCE_DATE_DEFAULTS,
      taxInceptionType: 'CostBasis'
    },
    buildCalculation: (id, options) => ({
      $type: 'tax-alpha',
      id,
      ...(options.taxInceptionType && { taxInceptionType: options.taxInceptionType }),
      contextChange: buildDateContextChange(options)
    }),
    columns: (id) => [
      { accessorKey: id, header: 'Tax Alpha', format: 'returns', align: 'right' }
    ],
    extractValue: (calc) => calc.taxAlpha
  },

  taxSavings: {
    type: 'taxSavings',
    label: 'Tax Savings',
    orionType: 'tax-savings',
    supportsDate: true,
    settingsSchema: [
      {
        key: 'taxSavingsValue',
        label: 'Savings Type',
        control: 'select',
        options: [
          { value: 'TaxLossHarvestingSavings', label: 'Tax Loss Harvesting' },
          { value: 'TaxTransitionSavings', label: 'Tax Transition' },
          { value: 'AssetLocationSavings', label: 'Asset Location' }
        ],
        defaultValue: 'TaxLossHarvestingSavings'
      }
    ],
    defaultOptions: {
      ...PERFORMANCE_DATE_DEFAULTS,
      taxSavingsValue: 'TaxLossHarvestingSavings'
    },
    buildCalculation: (id, options) => ({
      $type: 'tax-savings',
      id,
      ...(options.taxSavingsValue && { taxSavingsValue: options.taxSavingsValue }),
      contextChange: buildDateContextChange(options)
    }),
    columns: (id) => [
      { accessorKey: id, header: 'Tax Savings', format: 'marketValue', align: 'right' }
    ],
    extractValue: (calc) => calc.taxSavings
  },

  targetAllocation: {
    type: 'targetAllocation',
    label: 'Target Allocation',
    orionType: 'target-allocation',
    supportsDate: false,
    settingsSchema: [],
    defaultOptions: {},
    buildCalculation: (id) => ({
      $type: 'target-allocation',
      id
    }),
    columns: (id) => [
      { accessorKey: id, header: 'Target Weight', format: 'returns', align: 'right' },
      { accessorKey: `${id}_actual`, header: 'Actual Weight', format: 'returns', align: 'right' },
      { accessorKey: `${id}_drift`, header: 'Drift', format: 'returns', align: 'right' }
    ],
    extractValue: (calc) => calc.targetWeight,
    extractExtraValues: (calc, id) => ({
      [`${id}_actual`]: calc.actualWeight,
      [`${id}_drift`]: calc.drift
    })
  }
}

let instanceCounter = 0
export function createColumnInstance (type, optionOverrides = {}) {
  const typeDef = CALCULATION_TYPES[type]
  if (!typeDef) return null
  return {
    type,
    id: `${type}_${instanceCounter++}`,
    options: { ...typeDef.defaultOptions, ...optionOverrides }
  }
}

export const GROUPING_PRESETS = [
  { value: 'account', label: 'By Account', groupings: ['Account'] },
  { value: 'asset-class', label: 'By Asset Class', groupings: ['Asset Class'] },
  { value: 'asset-class-product', label: 'By Asset Class > Product', groupings: ['Asset Class', 'Product'] },
  { value: 'custodian-account', label: 'By Custodian > Account', groupings: ['Custodian', 'Account'] },
  { value: 'account-asset-class', label: 'By Account > Asset Class', groupings: ['Account', 'Asset Class'] },
  { value: 'account-type', label: 'By Account Type', groupings: ['Account Type'] },
  { value: 'custom', label: 'Custom...' }
]

export const DEFAULT_CONFIG = {
  groupings: ['Account'],
  groupingPreset: 'account',
  columns: [
    createColumnInstance('marketValue'),
    createColumnInstance('performance')
  ],
  dateMode: 'quickDate',
  quickDate: 'YTD',
  startDate: null,
  endDate: null,
  fetchAllLevels: false
}
